import axios from 'axios';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Plus, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { formatLocalTime } from '@/utils/formatLocalTime'; // 🆕 [P1 SUITE+]

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const JOURS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

export function CalendrierConges() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [demandes, setDemandes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showCreer, setShowCreer] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEmployesEnConge, setShowEmployesEnConge] = useState(false);

  const [nouvelleDemande, setNouvelleDemande] = useState({
    employe_id: '',
    employe_nom: '',
    type_conge: 'vacances',
    date_debut: '',
    date_fin: '',
    notes_employe: '',
  });

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  const fetchData = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const [demandesRes, empRes] = await Promise.all([
        axios.get(`${API_URL}/api/calendrier/conges?year=${year}&month=${month}`),
        axios.get(`${API_URL}/api/employees`),
      ]);

      if (demandesRes.status === 200) {
        const data = demandesRes.data;
        setDemandes(data.demandes || []);
      }

      if (empRes.status === 200) {
        const data = empRes.data;
        setEmployees(data.employees || []);
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const creerDemande = async () => {
    if (!nouvelleDemande.employe_id || !nouvelleDemande.date_debut) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/calendrier/conges`, nouvelleDemande);

      if (response.status !== 200) throw new Error('Erreur');
      toast.success('Demande de congé créée!');
      setShowCreer(false);
      fetchData();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur');
    }
  };

  const approuverDemande = async (demandeId) => {
    try {
      const response = await axios.patch(
        `${API_URL}/api/calendrier/conges/${demandeId}/approuver?approuve_par=Manager`
      );
      if (response.status !== 200) throw new Error('Erreur');
      toast.success('Demande approuvée!');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDayClick = (day) => {
    const conges = getCongesForDate(day);
    if (conges.length > 0) {
      setSelectedDate(day);
      setShowEmployesEnConge(true);
    }
  };

  const getEmployesEnConge = () => {
    if (!selectedDate) return [];
    return getCongesForDate(selectedDate);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null);
    for (let day = 1; day <= daysInMonth; day++) days.push(new Date(year, month, day));
    return days;
  };

  const getCongesForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return demandes.filter(d => dateStr >= d.date_debut && dateStr <= d.date_fin && d.statut === 'approuve');
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="p-2 sm:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Calendrier des Congés</h1>
          <p className="text-gray-600 mt-1 text-sm">Gestion des congés employés</p>
        </div>
        <Button onClick={() => setShowCreer(true)} className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle Demande
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={() => { const d = new Date(currentDate); d.setMonth(d.getMonth() - 1); setCurrentDate(d); }}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <h2 className="text-xl font-bold">{MOIS[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
            <Button variant="outline" onClick={() => { const d = new Date(currentDate); d.setMonth(d.getMonth() + 1); setCurrentDate(d); }}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-7 gap-2 mb-2">
            {JOURS.map((j) => <div key={j} className="text-center font-semibold text-sm">{j}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {days.map((day, index) => {
              if (!day) return <div key={`empty-${index}`} className="aspect-square" />;
              const isToday = day.toDateString() === new Date().toDateString();
              const conges = getCongesForDate(day);
              const hasConges = conges.length > 0;
              return (
                <div
                  key={day.toISOString()}
                  onClick={() => handleDayClick(day)}
                  className={`aspect-square border rounded-lg p-1 transition-colors ${
                    isToday ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
                  } ${hasConges ? 'cursor-pointer hover:bg-orange-50 hover:border-orange-400' : ''}`}
                >
                  <p className="text-sm font-semibold">{day.getDate()}</p>
                  {hasConges && (
                    <Badge className="text-xs bg-orange-100 text-orange-800">
                      {conges.length}
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Demandes de Congés</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {demandes.slice(0, 10).map((d) => (
              <div key={d.id} className="p-3 border rounded flex justify-between items-center">
                <div>
                  <p className="font-semibold">{d.employe_nom}</p>
                  <p className="text-sm text-gray-600">
                    {d.date_debut} → {d.date_fin} ({d.nombre_jours} jours)
                  </p>
                  <Badge>{d.type_conge}</Badge>
                </div>
                <div className="flex gap-2">
                  {d.statut === 'en_attente' ? (
                    <>
                      <Button size="sm" className="bg-green-600" onClick={() => approuverDemande(d.id)}>
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline">
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <Badge className={d.statut === 'approuve' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {d.statut === 'approuve' ? 'Approuvé' : 'Refusé'}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={showCreer} onOpenChange={setShowCreer}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle Demande de Congé</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Employé</Label>
              <Select value={nouvelleDemande.employe_id} onValueChange={(val) => {
                const emp = employees.find(e => e.id === val);
                setNouvelleDemande({ ...nouvelleDemande, employe_id: val, employe_nom: emp ? `${emp.prenom} ${emp.nom}` : '' });
              }}>
                <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date Début</Label>
                <Input type="date" value={nouvelleDemande.date_debut} onChange={(e) => setNouvelleDemande({ ...nouvelleDemande, date_debut: e.target.value })} />
              </div>
              <div>
                <Label>Date Fin</Label>
                <Input type="date" value={nouvelleDemande.date_fin} onChange={(e) => setNouvelleDemande({ ...nouvelleDemande, date_fin: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreer(false)}>Annuler</Button>
              <Button onClick={creerDemande}>Créer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialogue pour afficher les employés en congé un jour donné */}
      <Dialog open={showEmployesEnConge} onOpenChange={setShowEmployesEnConge}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedDate && `Employés en congé le ${formatLocalTime(selectedDate.toISOString(), { dateOnly: true, weekday: 'long' })}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {getEmployesEnConge().length === 0 ? (
              <p className="text-center text-slate-500 py-4">Aucun employé en congé ce jour</p>
            ) : (
              getEmployesEnConge().map((demande, idx) => (
                <div key={demande.id || idx} className="p-3 border border-slate-200 rounded-lg bg-slate-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{demande.employe_nom}</p>
                      <div className="mt-1 space-y-1">
                        <p className="text-sm text-slate-600">
                          📅 {demande.date_debut} → {demande.date_fin}
                        </p>
                        <p className="text-sm text-slate-600">
                          🕒 {demande.nombre_jours} jour{demande.nombre_jours > 1 ? 's' : ''}
                        </p>
                        <Badge className="text-xs mt-1">
                          {demande.type_conge === 'vacances' ? '🏖️ Vacances' : 
                           demande.type_conge === 'maladie' ? '🤒 Maladie' : 
                           demande.type_conge === 'personnel' ? '👤 Personnel' : demande.type_conge}
                        </Badge>
                      </div>
                    </div>
                    <Badge className={
                      demande.statut === 'approuve' ? 'bg-green-100 text-green-800' : 
                      demande.statut === 'refuse' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }>
                      {demande.statut === 'approuve' ? 'Approuvé' : 
                       demande.statut === 'refuse' ? 'Refusé' : 'En attente'}
                    </Badge>
                  </div>
                  {demande.notes_employe && (
                    <p className="text-sm text-slate-600 mt-2 pl-2 border-l-2 border-slate-300">
                      💬 {demande.notes_employe}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setShowEmployesEnConge(false)}>
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
